import { KeyRound } from "lucide-react";

import type { LicenceStatus } from "../api/types";
import { Callout } from "../components/Callout";
import { Presence } from "../components/presence";
import { t } from "../i18n/i18n";
import { formatLicenceDay } from "./licence";
import { useLicenceSummary } from "./LicenceFooter";
import { readOnlyInfo } from "./readOnly";
import { trialExport } from "./trial";

/** Licence d'essai sur l'accueil (demande d'Alexis du 30/10/2026, registre F44) : « Licence d'essai », la clé de
 *  licence (telle que la fiche de la licence la montre : masquée en mode comptes ou après l'émission d'un code
 *  d'amorçage) et sa validité (jour de l'échéance à l'heure de Paris, jours restants). Rien pour une autre licence ;
 *  licence d'essai échue, le bandeau de la lecture seule prend le relais. */

const DAY_MS = 24 * 3600 * 1000;

/** Jours restants jusqu'à l'échéance (arrondis au jour supérieur, 0 une fois passée). */
export function daysLeft(expiresAt: string, now = Date.now()): number {
  return Math.max(0, Math.ceil((new Date(expiresAt).getTime() - now) / DAY_MS));
}

/** « encore 12 jours », « encore 1 jour », « dernier jour ». */
export function daysLeftText(days: number): string {
  if (days <= 0) return t("dernier jour");
  return days === 1 ? t("encore 1 jour") : t("encore {n} jours", { n: days });
}

/** Ce que l'encart montre, ou null hors licence d'essai en cours. */
export function trialSummary(status: LicenceStatus | undefined | null, now = Date.now()): { code: string | null; until: string; days: number } | null {
  if (!status || !trialExport(status) || readOnlyInfo(status) || !status.expires_at) return null;
  return { code: status.licence_code, until: formatLicenceDay(status.expires_at), days: daysLeft(status.expires_at, now) };
}

export function TrialLicenceCallout({ className }: { className?: string }) {
  const { status } = useLicenceSummary();
  const trial = trialSummary(status);
  return (
    <Presence show={Boolean(trial)}>
      {trial && (
        <Callout tone="info" icon={KeyRound} title={t("Licence d'essai")} className={className}>
          {trial.code && (
            <span className="block">
              {t("Clé de licence")} : <span className="font-mono text-ink">{trial.code}</span>
            </span>
          )}
          <span className="block">
            {t("Valable jusqu'au {date}", { date: trial.until })} · {daysLeftText(trial.days)}
          </span>
        </Callout>
      )}
    </Presence>
  );
}
